import type { AccessState, UserAccessSnapshot } from '@/features/commandDeck/contracts/types'

import {
  CONTROLLED_ACCESS_HOLDING_PATH,
  PATHWAY_ACCESS_SHELL_PATH,
} from './controlledAccessPaths'
import type { ControlledAccessHoldReason } from './accessPolicy'
import { resolveHoldReason, shouldUseDedicatedPathwayShell } from './accessPolicy'

export function buildControlledAccessHoldingHref(options: {
  redirectTo?: string
  reason?: ControlledAccessHoldReason
  accessState?: AccessState
}): string {
  const params = new URLSearchParams()
  if (options.redirectTo?.startsWith('/')) {
    params.set('redirectTo', options.redirectTo)
  }
  if (options.reason) {
    params.set('reason', options.reason)
  }
  if (options.accessState) {
    params.set('accessState', options.accessState)
  }
  const query = params.toString()
  return query ? `${CONTROLLED_ACCESS_HOLDING_PATH}?${query}` : CONTROLLED_ACCESS_HOLDING_PATH
}

export function buildPathwayShellHref(options: {
  redirectTo?: string
  accessState?: AccessState
}): string {
  const params = new URLSearchParams()
  if (options.redirectTo?.startsWith('/')) {
    params.set('redirectTo', options.redirectTo)
  }
  if (options.accessState) {
    params.set('accessState', options.accessState)
  }
  const query = params.toString()
  return query ? `${PATHWAY_ACCESS_SHELL_PATH}?${query}` : PATHWAY_ACCESS_SHELL_PATH
}

/**
 * Authenticated users without interior grant → pathway shell or generic holding.
 */
export function resolveAuthenticatedHoldHref(options: {
  redirectTo: string
  access: UserAccessSnapshot
  sessionError: string | null
}): string {
  if (shouldUseDedicatedPathwayShell(options.access)) {
    return buildPathwayShellHref({
      redirectTo: options.redirectTo,
      accessState: options.access.accessState,
    })
  }
  const reason = resolveHoldReason('authenticated', options.access, options.sessionError)
  return buildControlledAccessHoldingHref({
    redirectTo: options.redirectTo,
    reason,
    accessState: options.access.accessState,
  })
}

export function readPathwayShellSearchParams(search: string): {
  redirectTo: string
  accessState: AccessState | null
} {
  const params = new URLSearchParams(search)
  const redirectTo = params.get('redirectTo')
  const accessState = params.get('accessState')
  return {
    redirectTo: redirectTo?.startsWith('/') ? redirectTo : '/',
    accessState: accessState as AccessState | null,
  }
}

export function readControlledAccessSearchParams(search: string): {
  redirectTo: string
  reason: ControlledAccessHoldReason | null
  accessState: AccessState | null
} {
  const params = new URLSearchParams(search)
  const redirectTo = params.get('redirectTo')
  const reason = params.get('reason')
  const accessState = params.get('accessState')

  const validReasons = new Set(['signed_out', 'no_grant', 'session_error'])

  return {
    redirectTo: redirectTo?.startsWith('/') ? redirectTo : '/',
    reason:
      reason && validReasons.has(reason) ? (reason as ControlledAccessHoldReason) : null,
    accessState: accessState as AccessState | null,
  }
}
